import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { audio } from '../core/audio';
import { recordSkill } from '../core/storage';

// Loud vs soft drums: each drum plays in turn, child taps the loud one. No fail state.
export function LoudSoft({ activity, onComplete }) {
  const [playing, setPlaying] = useState(null);
  const [state, setState] = useState('playing');
  const [shake, setShake] = useState(0);
  const stats = useRef({ attempts: 0 });
  const busy = useRef(false);

  // One drum. `vol` carries loud-vs-soft; `beats`+`gap` carry fast-vs-slow, so
  // the same component serves both questions. A single beat still waits for the
  // sample to finish, which is the original behaviour.
  const playDrum = (d) => new Promise((resolve) => {
    const beats = d.beats || 1;
    const gap = d.gap || 0;
    let i = 0;
    const hit = () => {
      const a = new Audio('/assets/sfx/snd_drum.wav');
      a.volume = d.vol ?? 1;
      if (beats === 1) { a.onended = resolve; a.onerror = resolve; }
      a.play().catch(() => {});
      i += 1;
      if (i < beats) setTimeout(hit, gap);
      else if (beats > 1) setTimeout(resolve, gap);
    };
    hit();
  });

  const demo = async () => {
    for (const d of activity.drums) {
      setPlaying(d.id);
      await playDrum(d);
      await new Promise((r) => setTimeout(r, 350));
    }
    setPlaying(null);
    audio.playVoice(activity.voice, activity.voiceAlt);
  };

  useEffect(() => { const t = setTimeout(demo, 500); return () => clearTimeout(t); }, []); // eslint-disable-line

  const tap = async (d) => {
    if (busy.current || state === 'correct') return;
    stats.current.attempts += 1;
    if (d.id === activity.correct) {
      busy.current = true;
      setState('correct');
      audio.playSfx('correct');
      recordSkill(activity.skill, true);
      window.dispatchEvent(new CustomEvent('pp-answer', { detail: { correct: true } }));
      await audio.playVoice('correct1');
      onComplete({ id: activity.id, correct: true, attempts: stats.current.attempts, hints: 0, skill: activity.skill });
    } else {
      recordSkill(activity.skill, false);
      window.dispatchEvent(new CustomEvent('pp-answer', { detail: { correct: false } }));
      setShake((s) => s + 1);
      // Play the drum the child actually tapped. At age 2 a child cannot hold
      // two sounds in memory and compare them — they learn by touching one and
      // hearing it. Silence on a wrong tap teaches nothing.
      setPlaying(d.id);
      const a = new Audio('/assets/sfx/snd_drum.wav');
      a.volume = d.vol;
      a.onended = () => setPlaying(null);
      a.onerror = () => setPlaying(null);
      a.play().catch(() => setPlaying(null));
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 px-5" data-testid={`activity-${activity.id}`}>
      <motion.button whileTap={{ scale: 0.9 }} data-testid="play-prompt-sound-btn" onClick={demo}
        className="w-24 h-24 rounded-full bg-white/90 backdrop-blur flex items-center justify-center"
        style={{ boxShadow: 'inset 0 -6px 0 rgba(0,0,0,0.1), 0 10px 24px rgba(29,53,87,0.2)' }}>
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#45B6FE" strokeWidth="2.6" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3" fill="#45B6FE" /></svg>
      </motion.button>
      <div className="flex gap-6">
        {activity.drums.map((d) => (
          <motion.button key={d.id} data-testid={`choice-${d.id}`}
            animate={playing === d.id ? { scale: [1, d.vol > 0.6 ? 1.22 : 1.06, 1] } : state === 'correct' && d.id === activity.correct ? { scale: [1, 1.18, 1.1] } : {}}
            transition={{ duration: 0.4 }}
            onClick={() => tap(d)}
            className="rounded-[2rem] p-4 bg-white/70 backdrop-blur-md min-w-[110px] min-h-[110px] flex items-center justify-center"
            style={{ border: '5px solid rgba(255,255,255,0.9)', boxShadow: state === 'correct' && d.id === activity.correct ? '0 0 0 6px #FFD166' : 'inset 0 -6px 0 rgba(0,0,0,0.08), 0 10px 24px rgba(29,53,87,0.15)' }}>
            <motion.div key={shake} animate={{ x: [0, 0] }}>
              <svg width={d.vol > 0.6 ? 104 : 84} height={d.vol > 0.6 ? 84 : 70} viewBox="0 0 100 80">
                <ellipse cx="50" cy="22" rx="42" ry="14" fill="#FFD166" stroke="#00000022" strokeWidth="2" />
                <path d="M8 22 L8 58 Q50 76 92 58 L92 22 Q50 40 8 22 Z" fill="#FF5A5A" />
                <ellipse cx="50" cy="22" rx="42" ry="14" fill="#FFF3D6" />
              </svg>
            </motion.div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
